<script setup lang="ts">
import { computed, onMounted, toRaw } from 'vue'

import { useReportCellFormatter } from '../../composables/useReportCellFormatter'
import type { ReportColumn } from '../../models/column.types'
import type { ReportFilter } from '../../models/filter.types'
import type { ReportRow } from '../../models/report.types'
import type { ReportTableType } from '../../models/tablePresentation.types'
import { resolveCellLink } from '../../service/cellLink'
import {
  buildComparisonTotalSources,
  getRenderedComparisonFields,
  isComparisonChangeColumn,
} from '../../service/comparisonColumns'
import {
  inlineBreakdownSnapshot,
  inlineCellColumn,
  restoreBreakdownColumn,
} from '../../service/inlineBreakdown'
import { collapseReportMediaColumns } from '../../service/mediaColumns'
import {
  isBadgeColumn,
  isChipListColumn,
  isMediaColumn,
  isPercentileColumn,
  isPerformanceScoreColumn,
  isStatusColumn,
  isWindowCellsColumn,
} from '../../service/reportCellKind'
import { resolveRowMedia } from '../../service/reportMediaPresentation'
import {
  buildHeatmapCells,
  buildReportTotalRows,
  type ReportHeatmapCellState,
} from '../../service/tablePresentation'
import { createReportTableRowKeyResolver } from '../../service/tableRowIdentity'

import BadgeCell from './cells/BadgeCell.vue'
import CellFilterAction from './cells/CellFilterAction.vue'
import ChipListCell from './cells/ChipListCell.vue'
import DeltaCell from './cells/DeltaCell.vue'
import MediaCell from './cells/MediaCell.vue'
import MediaGalleryCell from './cells/MediaGalleryCell.vue'
import PercentileCell from './cells/PercentileCell.vue'
import PerformanceScoreCell from './cells/PerformanceScoreCell.vue'
import StatusCell from './cells/StatusCell.vue'
import WindowCellsCell from './cells/WindowCellsCell.vue'
import MatrixTable from './MatrixTable.vue'
import ReportHeaderLabel from './ReportHeaderLabel.vue'
import ReportHeatmapCell from './ReportHeatmapCell.vue'

import { useI18n } from '@/shared/i18n'
import { markFirstRow } from '@/shared/lib/perf/perfMarks'
import type { SortCycle, SortDirection, SortState, TableColumn } from '@/shared/ui'
import { PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui/logo'
import type { UiAsyncState } from '@/shared/ui/types'

const props = withDefaults(
  defineProps<{
    rows: ReportRow[]
    columns: ReportColumn[]
    ariaLabel: string
    state?: UiAsyncState
    refreshing?: boolean
    sort?: SortState
    draggable?: boolean
    sortCycle?: SortCycle
    initialSortDirection?: SortDirection
    allowColumnResize?: boolean
    tableType?: ReportTableType
    breakdownField?: string | null
    matrixDimension?: string | null
    pivotMetric?: string | null
    total?: Record<string, unknown> | null
    totalLabel?: string
    otherTotal?: Record<string, unknown> | null
    grandTotal?: Record<string, unknown> | null
    ga4Total?: Record<string, unknown> | null
    totalRowKinds?: readonly ('other' | 'ga4')[]
    heatmap?: boolean | readonly string[]
    maxHeight?: string
    totalRows?: number | null
    channel?: string
    /** Reveals a hover "Filter" action on dimension cells (v3 `OrphexTd` `showTdFilter`). */
    filterable?: boolean
  }>(),
  {
    state: 'ready',
    sort: null,
    draggable: false,
    sortCycle: 'binary',
    initialSortDirection: 'asc',
    allowColumnResize: true,
    tableType: 'table',
    breakdownField: null,
    matrixDimension: null,
    pivotMetric: null,
    total: null,
    otherTotal: null,
    grandTotal: null,
    ga4Total: null,
    totalRowKinds: () => ['other', 'ga4'],
    heatmap: false,
    channel: '',
  },
)

const emit = defineEmits<{
  'update:sort': [sort: SortState]
  reorder: [columns: TableColumn<ReportRow>[]]
  resize: [columns: TableColumn<ReportRow>[]]
  'load-more': []
  'add-filter': [filter: ReportFilter]
}>()

const { t } = useI18n()
const { formatCell } = useReportCellFormatter()
const presentation = computed(() =>
  inlineBreakdownSnapshot({
    rows: props.rows,
    columns: props.columns,
    tableType: props.tableType,
    breakdownField: props.breakdownField,
    matrixDimension: props.matrixDimension,
    pivotMetric: props.pivotMetric,
  }),
)
const LINK_CLASS = 'report-table__link cursor-pointer hover:text-highlight'
const presentedColumns = computed(() =>
  collapseReportMediaColumns(presentation.value.columns, t('reports.table.media')),
)
const comparisonFields = computed(() => getRenderedComparisonFields(presentedColumns.value))

function cellLink(row: ReportRow, column: ReportColumn): string | null {
  return column.url === undefined ? null : resolveCellLink(row[column.url])
}

function linkAttrs(row: ReportRow, column: ReportColumn): Record<string, string> {
  const href = cellLink(row, column)
  return href === null ? {} : { href, target: '_blank', rel: 'noopener noreferrer' }
}

function leadingLogo(row: ReportRow, column: ReportColumn): { src: string; label: string } | null {
  if (column.numeric === true || presentedColumns.value[0]?.field !== column.field) return null
  // A child row carries its own channel only when the breakdown IS the channel.
  const child = presentation.value.children.has(toRaw(row))
  if (child && props.breakdownField !== 'marketing_channel') return null
  const channel = row['marketing_channel']
  if (typeof channel !== 'string' || channel.trim() === '') return null
  const src = resolveStaticPlatformLogo(channel)
  return src === undefined ? null : { src, label: channel }
}
const presentedRows = computed(() => presentation.value.rows)

onMounted(() => {
  if (presentedRows.value.length > 0) markFirstRow()
})
const expandable = computed(
  () =>
    props.tableType !== 'matrix_table' &&
    Boolean(props.breakdownField) &&
    presentedRows.value.some((row) => (row.edges?.length ?? 0) > 0),
)
const resolveRowKey = computed(() =>
  createReportTableRowKeyResolver(
    presentedRows.value,
    presentedColumns.value,
    props.breakdownField,
  ),
)
const footerRows = computed(() =>
  props.tableType === 'matrix_table'
    ? []
    : buildReportTotalRows(
        presentedColumns.value,
        buildComparisonTotalSources(
          {
            total: props.total,
            otherTotal: props.totalRowKinds.includes('other') ? props.otherTotal : null,
            grandTotal: props.grandTotal,
            ga4Total: props.totalRowKinds.includes('ga4') ? props.ga4Total : null,
          },
          comparisonFields.value,
        ),
        {
          total: props.totalLabel || t('reports.table.total'),
          otherTotal: t('reports.table.otherTotal'),
          grandTotal: t('reports.table.grandTotal'),
          ga4Total: t('reports.table.ga4Total'),
        },
      ),
)
const heatmapCellsByRow = computed(() => {
  const cells = new WeakMap<ReportRow, Record<string, ReportHeatmapCellState>>()
  if (!props.heatmap) return cells
  const fields = Array.isArray(props.heatmap) ? props.heatmap : undefined

  function indexRows(rows: readonly ReportRow[]): void {
    const states = buildHeatmapCells(rows, presentedColumns.value, fields)
    rows.forEach((row, index) => cells.set(toRaw(row), states[index] ?? {}))
  }

  indexRows(presentedRows.value)
  indexRows(presentedRows.value.flatMap((row) => row.edges ?? []))
  return cells
})

function heatmapCell(row: ReportRow, column: ReportColumn): ReportHeatmapCellState | undefined {
  return heatmapCellsByRow.value.get(toRaw(row))?.[column.field]
}

function childrenFor(row: ReportRow): ReportRow[] {
  return row.edges ?? []
}

function rowKey(row: ReportRow, index: number): string {
  return resolveRowKey.value(row, index)
}

function expandLabel(row: ReportRow): string {
  return t('reports.table.expandBreakdown', { value: rowLabel(row) })
}

function collapseLabel(row: ReportRow): string {
  return t('reports.table.collapseBreakdown', { value: rowLabel(row) })
}

function rowLabel(row: ReportRow): string {
  const dimension = presentedColumns.value.find((column) => !column.numeric)
  return dimension ? formatCell(row[dimension.field], dimension) : ''
}

/** With a row, an inlined breakdown child's label cell formats as the breakdown column. */
function cellDisplay(value: unknown, column: ReportColumn, row?: ReportRow): string {
  return formatCell(value, row ? inlineCellColumn(presentation.value, toRaw(row), column) : column)
}

function hasGallery(row: ReportRow): boolean {
  return resolveRowMedia(row).length > 1
}

function mediaValue(row: ReportRow, value: unknown): unknown {
  if (typeof value === 'string' && value.trim() !== '') return value
  return resolveRowMedia(row)[0]?.thumbnail ?? value
}

function mediaAlt(row: ReportRow, column: ReportColumn): string {
  const labelColumn = presentedColumns.value.find(
    (candidate) =>
      candidate.field !== column.field && !candidate.numeric && !isMediaColumn(candidate),
  )
  const label = labelColumn ? cellDisplay(row[labelColumn.field], labelColumn) : ''
  return label ? `${column.header}: ${label}` : column.header
}

function heatmapLabel(
  value: unknown,
  column: ReportColumn,
  intensity: 'low' | 'medium' | 'high',
): string {
  return t('reports.table.heatmapCell', {
    value: cellDisplay(value, column),
    intensity: t(`reports.table.heatmapIntensity.${intensity}`),
  })
}
</script>

<template>
  <MatrixTable
    :rows="presentedRows"
    :footer-rows="footerRows"
    :columns="presentedColumns"
    v-bind="{
      ariaLabel,
      ...(maxHeight === undefined ? {} : { maxHeight }),
      ...(totalRows === undefined || totalRows === null ? {} : { totalRows }),
    }"
    :state="state"
    :refreshing="refreshing"
    :sort="sort"
    :draggable="tableType === 'matrix_table' ? false : draggable"
    :sort-cycle="sortCycle"
    :initial-sort-direction="initialSortDirection"
    :allow-column-resize="allowColumnResize"
    :channel="channel"
    :row-key="rowKey"
    :expandable="expandable"
    :get-children="childrenFor"
    :expand-label="expandLabel"
    :collapse-label="collapseLabel"
    @update:sort="emit('update:sort', $event)"
    @reorder="emit('reorder', restoreBreakdownColumn($event, columns, breakdownField))"
    @resize="emit('resize', $event)"
    @load-more="emit('load-more')"
  >
    <template #empty><slot name="empty" /></template>
    <template #error="slotProps"><slot name="error" v-bind="slotProps" /></template>
    <template
      v-for="column in presentedColumns"
      :key="`header-${column.field}`"
      #[`header-${column.field}`]="header"
    >
      <slot :name="`header-${column.field}`" v-bind="header">
        <slot name="header" v-bind="header"
          ><ReportHeaderLabel :column="column" :channel="channel"
        /></slot>
      </slot>
    </template>
    <template
      v-for="column in presentedColumns"
      :key="column.field"
      #[`cell-${column.field}`]="cell"
    >
      <slot :name="`cell-${column.field}`" v-bind="{ ...cell, column }">
        <slot name="cell" v-bind="{ ...cell, column }">
          <DeltaCell v-if="isComparisonChangeColumn(column)" :row="cell.row" :column="column" />
          <StatusCell v-else-if="isStatusColumn(column)" :row="cell.row" :column="column" />
          <BadgeCell v-else-if="isBadgeColumn(column)" :row="cell.row" :column="column" />
          <WindowCellsCell
            v-else-if="isWindowCellsColumn(column)"
            :row="cell.row"
            :column="column"
          />
          <ChipListCell v-else-if="isChipListColumn(column)" :row="cell.row" :column="column" />
          <MediaGalleryCell
            v-else-if="isMediaColumn(column) && hasGallery(cell.row)"
            :row="cell.row"
            :alt="mediaAlt(cell.row, column)"
          />
          <MediaCell
            v-else-if="isMediaColumn(column)"
            :value="mediaValue(cell.row, cell.value)"
            :alt="mediaAlt(cell.row, column)"
          />
          <ReportHeatmapCell
            v-else-if="heatmapCell(cell.row, column)"
            :display-value="cellDisplay(cell.value, column, cell.row)"
            :accessibility-label="
              heatmapLabel(cell.value, column, heatmapCell(cell.row, column)?.intensity ?? 'low')
            "
            :intensity="heatmapCell(cell.row, column)?.intensity ?? 'low'"
            :ratio="heatmapCell(cell.row, column)?.ratio ?? 0"
          />
          <PerformanceScoreCell
            v-else-if="isPerformanceScoreColumn(column)"
            :row="cell.row"
            :column="column"
          />
          <PercentileCell v-else-if="isPercentileColumn(column)" :row="cell.row" :column="column" />
          <span
            v-else-if="leadingLogo(cell.row, column)"
            class="report-table__leading flex min-w-0 items-center gap-xs"
          >
            <PlatformLogo
              :platform="leadingLogo(cell.row, column)!.label"
              :src="leadingLogo(cell.row, column)!.src"
              :alt="leadingLogo(cell.row, column)!.label"
              :size="17"
              class="shrink-0"
            />
            <component
              :is="cellLink(cell.row, column) ? 'a' : 'span'"
              v-bind="linkAttrs(cell.row, column)"
              class="min-w-0 truncate"
              :class="cellLink(cell.row, column) ? LINK_CLASS : undefined"
            >
              {{ cellDisplay(cell.value, column, cell.row) }}
            </component>
          </span>
          <component
            :is="cellLink(cell.row, column) ? 'a' : 'span'"
            v-else
            v-bind="linkAttrs(cell.row, column)"
            :class="cellLink(cell.row, column) ? LINK_CLASS : undefined"
            >{{ cellDisplay(cell.value, column, cell.row) }}</component
          >
          <CellFilterAction
            v-if="filterable && tableType !== 'matrix_table'"
            :column="column"
            :value="cell.value"
            @add-filter="emit('add-filter', $event)"
          />
        </slot>
      </slot>
    </template>
    <template
      v-for="column in presentedColumns"
      :key="`footer-${column.field}`"
      #[`footer-${column.field}`]="footer"
    >
      <slot :name="`footer-${column.field}`" v-bind="footer">
        <slot name="footer" v-bind="footer">
          <DeltaCell v-if="isComparisonChangeColumn(column)" :row="footer.row" :column="column" />
          <template v-else>{{ cellDisplay(footer.value, column) }}</template>
        </slot>
      </slot>
    </template>
  </MatrixTable>
</template>
